/**
 * Admin Login Page
 * Dedicated login for Admin Portal
 * Completely isolated from CRM login
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { consumeReturnPath, consumeSessionNotice, NOTICE_TEXT } from './sessionExpiry';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../../components/ui/alert';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { adminLogin, isAuthenticated, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to go after signing in: the path the guard was protecting, else the
  // path the provider remembered when the session ended, else the dashboard.
  // Read ONCE at mount — consuming clears the stored copy — so a second
  // render cannot lose it.
  const [returnTo] = useState(() => {
    const fromState = location?.state?.from;
    const stored = consumeReturnPath();
    const candidate = (typeof fromState === 'string' && fromState.startsWith('/') && !fromState.startsWith('//'))
      ? fromState : stored;
    return candidate && candidate !== '/login' ? candidate : '/';
  });
  // Why the operator is looking at this page again, if the session ended on
  // its own. Shown once, then forgotten.
  const [notice] = useState(() => {
    const reason = consumeSessionNotice();
    return reason ? (NOTICE_TEXT[reason] || NOTICE_TEXT.expired) : '';
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await adminLogin(email, password);
    
    if (result.success) {
      navigate(returnTo, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyem0tNiA2aC00djJoNHYtMnptMC02aC00djJoNHYtMnptMC02aC00djJoNHYtMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
      
      <Card className="w-full max-w-md relative z-10 bg-slate-800/90 backdrop-blur-xl border-slate-700 shadow-2xl">
        <CardHeader className="text-center space-y-4 pb-2">
          {/* THE "Back to CRM" LINK WAS REMOVED ON 8 SEP 2026, ON PURPOSE.
            *
            * It read `REACT_APP_CRM_URL`, which is baked at BUILD time. One
            * Admin build serves LIVE, STAGING, DEMO and DEVELOPMENT, so the
            * single value in it was wrong for three of the four: the deployed
            * bundle carried the DEVELOPMENT app host, and an operator working
            * on LIVE tenants who clicked this was sent there. (Its localhost
            * fallback was worse still, and only invisible because the build
            * happened to set the variable.)
            *
            * `test_environment_contract_mirror` already scans this tree for
            * environment hostnames and could not see it: the host was never a
            * literal here, it arrived from the environment at build time. That
            * is the deeper reason to delete the read rather than repoint it —
            * a value that only exists after a build is a value no source scan
            * can check. Every line of this note starts with `*` because that
            * same scanner skips comment lines by prefix, and the first draft of
            * it tripped the guard by naming the host in prose.
            *
            * It cannot be fixed by resolving the right host, because this page
            * is PRE-AUTH: nobody has said which environment they mean yet, and
            * the environment registry that owns hostnames is behind
            * authentication. There is no unauthenticated resolver — /api/config,
            * /api/admin/config, /api/public-config and /api/environments all
            * 404 — and inventing a public config endpoint to serve one
            * convenience link is a larger surface than the link is worth.
            *
            * So the link is gone rather than relocated. Operators reach the CRM
            * by its own address; nothing in Admin now claims to know which one
            * that is before they have signed in. `environmentRegistry` remains
            * the ONE hostname authority, and this component no longer carries a
            * hostname of any kind. Pinned by `adminLoginNoEnvironmentUrl.test.jsx`.
            */}

          {/* Logo */}
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Shield className="h-8 w-8 text-white" />
          </div>
          
          <div>
            <CardTitle className="text-2xl font-bold text-white">
              Admin Portal
            </CardTitle>
            <CardDescription className="text-slate-400 mt-2">
              Sign in with your administrator credentials
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4">
          {notice && !error && (
            <Alert className="mb-4 bg-amber-500/10 border-amber-500/50 text-amber-300" data-testid="admin-session-notice">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-500/10 border-red-500/50 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:ring-indigo-500/20"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium h-11 shadow-lg shadow-indigo-500/25"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In to Admin Portal'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Platform administrators only. Authorized access required.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
